import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, MenuItem } from '@mui/material';
import { Form, Formik } from 'formik';
import { colors } from '../../assets/utils/colors';
import CustomCard2 from '../../components/cards/CustomCard2';
import CustomeFileForm from '../../components/forms/CustomeFileForm';
import CustomeMultlineForm from '../../components/forms/CustomeMultlineForm';
import CustomTextField from '../../components/forms/CustomTextField';
import PageLayout from '../layouts/PageLayout';
import * as Yup from 'yup';
import { Image } from '@mui/icons-material';
import CustomSelectForm from '../../components/forms/CustomSelectForm';
import ProductDetailsSidebar from '../../components/sidebars/ProductDetailsSidebar';
import { useDispatch, useSelector } from 'react-redux';
import { filter, map, size } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useGetSingleProductQuery,
    useUpdateProductMutation,
} from '../../api/products/productAPI';
import { setProduct } from '../../features/productSlice';
import { toast } from 'react-hot-toast';
import CustomSubmitButton from '../../components/forms/CustomSubmitButton';

// ################### validation schema ###############
const updateProductSchema = Yup.object({
    brand_id: Yup.string().required('Brand is required'),
    category_id: Yup.string().required('Category is required'),
    branch_id: Yup.string().required('Branch is required'),
    sub_category_id: Yup.string().required('Sub category is required'),
    product_name: Yup.string().required('Name is required'),
    product_description: Yup.string().required('Description is required'),
    //   file: Yup.mixed().required('Product image is required'),
    product_weight: Yup.string().required('Weight is required'),
    product_sku: Yup.string().required('SKU is required'),
    product_quantity: Yup.string().required('Product quantity is required'),
    expire_date: Yup.date().min(new Date()).required('Expire date is required'),
    product_price: Yup.string().required('Price is required'),
    isOnSale: Yup.boolean().required('Identfy if product is onsell'),
    discounted_percent: Yup.string().max(
        2,
        'Discounted percent must be at most 2 characters',
    ),
});

// !################# MAIN FUNC ##################
const UpdateProduct = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { product_id } = useParams();
    const { data, isLoading, isSuccess } = useGetSingleProductQuery(product_id);
    const [
        updateProduct,
        { isSuccess: success, isLoading: loading, isError },
    ] = useUpdateProductMutation();

    const brands = useSelector((state) => state.brands.brands);
    const categories = useSelector((state) => state.categories.categories);
    const branches = useSelector((state) => state.branches.branches);
    const sub_categories = useSelector(
        (state) => state.categories.sub_categories,
    );

    const getSubCategories = (id) => {
        return filter(sub_categories, ({ category_id }) => category_id === id);
    };

    const product = useSelector((state) => state.products.product);

    // -> handle file preview
    const [filePreview, setFilePreview] = useState(
        product?.product_details?.product_banner,
    );

    useEffect(() => {
        if (isSuccess) {
            dispatch(setProduct({ product: data.data }));
        }

        if (success) {
            navigate(`/products/details/${product_id}`);
        }

        if (loading) {
            toast.success('Updating product details ... ', {
                icon: <CircularProgress size={15} color="secondary" />,
            });
        }

        if (isError) {
            toast.error(
                'Fail to update product details. Please try again later',
            );
        }

        if (isLoading) {
            toast.custom('Please wait while loading product details', {
                icon: <CircularProgress size={15} color="secondary" />,
                duration: 3000,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, isLoading, isError, loading, success]);

    return (
        <>
            <PageLayout
                sidebar={<ProductDetailsSidebar product={product} />}
                head="Update product details"
            >
                <CustomCard2 title={'Product details'} heightClear={640}>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            brand_id: product?.product_details?.brand_id,
                            branch_id: product?.product_details?.branch_id,
                            category_id: product?.product_details?.category_id,
                            sub_category_id:
                                product?.product_details?.sub_category_id,
                            product_name:
                                product?.product_details?.product_name,
                            product_description:
                                product?.product_details?.product_description,
                            file: '',
                            product_weight:
                                product?.product_details?.product_weight,
                            product_sku: product?.product_details?.product_sku,
                            product_quantity:
                                product?.product_details?.product_quantity,
                            expire_date: product?.product_details?.expire_date,
                            product_price:
                                product?.product_details?.product_price,
                            discounted_percent:
                                product?.product_details?.discounted_percent,
                            isOnSale: product?.product_details?.isOnSale,
                        }}
                        validationSchema={updateProductSchema}
                        onSubmit={async (values) => {
                            const formData = new FormData();

                            formData.append('product_id', product_id);
                            formData.append('product_banner', values.file);
                            formData.append('brand_id', values.brand_id);
                            formData.append('branch_id', values.branch_id);
                            formData.append('category_id', values.category_id);
                            formData.append(
                                'sub_category_id',
                                values.sub_category_id,
                            );
                            formData.append(
                                'product_name',
                                values.product_name,
                            );
                            formData.append(
                                'product_description',
                                values.product_description,
                            );
                            formData.append(
                                'product_weight',
                                values.product_weight,
                            );
                            formData.append('product_sku', values.product_sku);
                            formData.append(
                                'product_quantity',
                                values.product_quantity,
                            );
                            formData.append('expire_date', values.expire_date);
                            formData.append(
                                'product_price',
                                values.product_price,
                            );
                            formData.append('isOnSale', values.isOnSale);
                            formData.append(
                                'discounted_percent',
                                values.discounted_percent,
                            );
                            await updateProduct(formData);
                        }}
                    >
                        {(formik) => (
                            <Form>
                                <Grid container padding={2} spacing={2}>
                                    <Grid item sm={8}>
                                        <Grid container spacing={2}>
                                            <Grid item sm={6}>
                                                <CustomTextField
                                                    name="product_name"
                                                    label={'Product name'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <CustomTextField
                                                    name="product_sku"
                                                    label={'Product SKU'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            {/* ############ | brand | category | subcategory| ############# */}
                                            <Grid item sm={3}>
                                                <CustomSelectForm
                                                    name="branch_id"
                                                    formikName="branch_id"
                                                    label="Branch"
                                                    formik={formik}
                                                >
                                                    {size(branches) > 0 ? (
                                                        map(
                                                            branches,
                                                            ({
                                                                branch_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {
                                                                        branch_name
                                                                    }{' '}
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <CustomSelectForm
                                                    name="brand_id"
                                                    formikName="brand_id"
                                                    label="Brand"
                                                    formik={formik}
                                                >
                                                    {size(brands) > 0 ? (
                                                        map(
                                                            brands,
                                                            ({
                                                                brand_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {brand_name}{' '}
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <CustomSelectForm
                                                    name="category_id"
                                                    formikName="category_id"
                                                    label="Category"
                                                    formik={formik}
                                                >
                                                    {size(categories) > 0 ? (
                                                        map(
                                                            categories,
                                                            ({
                                                                category_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {
                                                                        category_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={3}>
                                                {/* {getSubCategories(formik.values.category_id)} */}
                                                <CustomSelectForm
                                                    name="sub_category_id"
                                                    formikName="sub_category_id"
                                                    label="Sub category"
                                                    formik={formik}
                                                >
                                                    {size(
                                                        getSubCategories(
                                                            formik.values
                                                                .category_id,
                                                        ),
                                                    ) > 0 ? (
                                                        map(
                                                            getSubCategories(
                                                                formik.values
                                                                    .category_id,
                                                            ),
                                                            ({
                                                                id,
                                                                sub_category_name,
                                                            }) => (
                                                                <MenuItem
                                                                    value={id}
                                                                >
                                                                    {
                                                                        sub_category_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            {/* ############ |weight | price | discount | ############# */}
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_weight"
                                                    label={'Product weight'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_price"
                                                    label={'Product price'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="discounted_percent"
                                                    label={'Discount %'}
                                                    type="number"
                                                    color="primary"
                                                />
                                            </Grid>
                                            {/* ############ |quantity | Expire date | ############# */}
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_quantity"
                                                    label={'Product quantity'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name={'expire_date'}
                                                    label={'Expire date'}
                                                    type="date"
                                                    color="primary"
                                                    shrink
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <Grid container>
                                            <Grid item sm={12} sx={{ mt: 2 }}>
                                                <Box
                                                    sx={{
                                                        height: 225,
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        alignItems: 'center',
                                                        borderRadius: 5,
                                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor1})`,
                                                    }}
                                                >
                                                    {filePreview ? (
                                                        <>
                                                            <img
                                                                src={
                                                                    filePreview
                                                                }
                                                                alt="img"
                                                                style={{
                                                                    width:
                                                                        '100%',
                                                                    height:
                                                                        '10em',
                                                                    objectFit:
                                                                        'contain',
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Image
                                                                sx={{
                                                                    width:
                                                                        '100%',
                                                                    height:
                                                                        '7em',
                                                                    objectFit:
                                                                        'contain',
                                                                    opacity: 0.7,
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item sm={12}>
                                                <CustomeFileForm
                                                    name="product_banner"
                                                    label={'Product image'}
                                                    type="file"
                                                    color="primary"
                                                    setFilePreview={
                                                        setFilePreview
                                                    }
                                                    formik={formik}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={8}>
                                        <CustomeMultlineForm
                                            name="product_description"
                                            label={'Product description'}
                                            type="text"
                                            color="primary"
                                            rows={8}
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <Box
                                            sx={{
                                                height: 210,
                                                mt: 2,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CustomSubmitButton
                                                btnColor={'info'}
                                                loading={loading}
                                                onClick={formik.handleSubmit}
                                            >
                                                Update product
                                            </CustomSubmitButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </CustomCard2>
            </PageLayout>
        </>
    );
};

export default UpdateProduct;
